import { resolveFormStepData } from './resolveFormStepData'
import {
    ActionFailedEventDetail,
    ReplacerObject,
    SubmitFailedEventDetail,
    AnalyticsEventType,
    FormCompleteAnalyticsEvent,
    FormErrorAnalyticsEvent,
    FormInteractionAnalyticsEvent,
    FormProgressAnalyticsEvent,
    FormStartAnalyticsEvent,
    SearchAnalyticsEvent,
    SubmitSuccessEventDetail,
} from '../../../../../MyStichtingOpenHelpers/src/static'

type GetTextFn = ((key: string, replacer?: ReplacerObject, fallback?: string) => string) | undefined

/**
 * Yup says it returns error.message as a string, but at runtime it can be an object array.
 * This converts the message to a key and a replacer object.
 */
const messageToDictionaryArguments = (
    message: unknown,
): {
    key: string
    replacer?: ReplacerObject
} => {
    if (typeof message === 'string') return { key: message }
    if (message && typeof message === 'object' && !Array.isArray(message)) {
        const first = Object.values(message)[0]
        if (first && typeof first === 'object' && 'textKey' in first) {
            const { textKey, replacer } = first as { textKey: string; replacer?: ReplacerObject }
            return { key: textKey, replacer }
        }
    }
    return { key: String(message) }
}

export const mapToFormStartEvent = (formId: string): FormStartAnalyticsEvent => {
    const stepData = resolveFormStepData(formId)

    return {
        event: AnalyticsEventType.FORM_START,
        ...stepData,
        form_id: '',
    }
}

export const mapToFormProgressEvent = (formName: string, formId: string, count: number): FormProgressAnalyticsEvent => {
    const stepData = resolveFormStepData(formId)

    return {
        event: AnalyticsEventType.FORM_PROGRESS,
        ...stepData,
        form_id: '',
        count: String(count),
    }
}

export const mapSubmitSuccessToFormCompleteEvent = (
    detail: SubmitSuccessEventDetail,
    count: number,
): FormCompleteAnalyticsEvent => {
    const stepData = resolveFormStepData(detail.formId)

    return {
        event: AnalyticsEventType.FORM_COMPLETE,
        ...stepData,
        form_id: '',
        count: String(count),
    }
}

export const mapActionFailedToFormErrorEvent = (
    detail: ActionFailedEventDetail,
    count: number,
    getText: GetTextFn,
): FormErrorAnalyticsEvent => {
    const stepData = resolveFormStepData(detail.formId)

    return {
        event: AnalyticsEventType.FORM_ERROR,
        ...stepData,
        form_id: '',
        tm_field_name: detail.error.validationErrors?.map((error) => error.path).join(', ') ?? '',
        tm_value:
            detail.error.validationErrors
                ?.map((error) => {
                    const { key, replacer } = messageToDictionaryArguments(error.message)
                    return getText ? getText(key, replacer, key) : key
                })
                .join(', ') ?? '',
        tm_type: detail.error.validationErrors?.length ? 'formfieldError' : 'formError',
        tm_count: String(count),
    }
}

export const mapSubmitFailedToFormErrorEvent = (
    detail: SubmitFailedEventDetail,
    count: number,
    getText: GetTextFn,
): FormErrorAnalyticsEvent => {
    const stepData = resolveFormStepData(detail.formId)

    return {
        event: AnalyticsEventType.FORM_ERROR,
        ...stepData,
        form_id: '',
        tm_field_name: detail.validationErrors?.map((error) => error.path).join(', ') ?? '',
        tm_value:
            detail.validationErrors
                ?.map((error) => {
                    const { key, replacer } = messageToDictionaryArguments(error.message)
                    return getText ? getText(key, replacer, key) : key
                })
                .join(', ') ?? '',
        tm_type: detail.validationErrors?.length ? 'formfieldError' : 'formError',
        tm_count: String(count),
    }
}

export const mapToFormInteractionEvent = (
    formId: string,
    fieldName: string,
    value: string | undefined,
): FormInteractionAnalyticsEvent => {
    const stepData = resolveFormStepData(formId)

    return {
        event: AnalyticsEventType.FORM_INTERACTION,
        ...stepData,
        form_id: '',
        tm_field_name: fieldName,
        tm_value: value,
    }
}

export const mapToSearchAnalyticsEvent = (searchTerm: string): SearchAnalyticsEvent => ({
    event: AnalyticsEventType.SEARCH,
    search_term: searchTerm,
    tm_active_filters: '',
    tm_count: '',
})
